
import React, { useMemo, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, CheckCircle, AlertCircle, Clock, Eye, BarChart2, RefreshCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { el } from "date-fns/locale";
import { Lead } from "@/context/LeadContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { phpApi } from "@/integrations/php/client";

interface SMSTrackerProps {
  leads: Lead[];
}

interface SMSDeliveryStatus {
  id: string;
  sms_id: string;
  status: string;
  partner_id?: string;
  lead_id?: string;
  timestamp: string;
}

const SMSTracker: React.FC<SMSTrackerProps> = ({ leads }) => {
  const navigate = useNavigate();
  const [deliveryStatuses, setDeliveryStatuses] = useState<SMSDeliveryStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch SMS delivery statuses from the database
  useEffect(() => {
    fetchSMSDeliveryStatuses();
  }, []);

  const fetchSMSDeliveryStatuses = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await phpApi
        .from('sms_delivery_status')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(20);
      
      if (error) {
        console.error("Error fetching SMS delivery statuses:", error);
      } else if (data) {
        setDeliveryStatuses(data);
      }
    } catch (error) {
      console.error("Failed to fetch SMS delivery statuses:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Get updated lead statuses by combining lead and delivery status data
  const leadsWithStatus = useMemo(() => {
    return leads.map(lead => {
      const leadDeliveryStatuses = deliveryStatuses.filter(
        status => status.lead_id === lead.id
      );
      
      let smsStatus = "unknown";
      
      if (leadDeliveryStatuses.length > 0) {
        // Get the most recent status
        const latestStatus = leadDeliveryStatuses.sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        )[0];
        
        smsStatus = latestStatus.status;
      } else if (lead.smsStatus) {
        smsStatus = lead.smsStatus;
      }
      
      return {
        ...lead,
        smsStatus
      };
    });
  }, [leads, deliveryStatuses]);
  
  // Filter messages by status
  const sentSMS = leadsWithStatus.filter(lead => lead.smsStatus === "sent" || lead.smsStatus === "delivered");
  const deliveredSMS = leadsWithStatus.filter(lead => lead.smsStatus === "delivered");
  const failedSMS = leadsWithStatus.filter(lead => lead.smsStatus === "failed");
  const viewedSMS = leadsWithStatus.filter(lead => lead.viewed);
  
  // Calculate message engagement rate
  const messageEngagementRate = useMemo(() => {
    const totalSent = sentSMS.length;
    if (totalSent === 0) return 0;
    return Math.round((viewedSMS.length / totalSent) * 100);
  }, [sentSMS, viewedSMS]);
  
  // Sort SMS history by date (newest first)
  const smsHistory = [...sentSMS, ...failedSMS].sort((a, b) => 
    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  ).slice(0, 10); // Show only the last 10 for simplicity
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "sent":
        return <Clock className="h-4 w-4 text-blue-500" />;
      case "delivered":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "failed":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-500" />;
    }
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "sent":
        return <Badge variant="secondary">Απεστάλη</Badge>;
      case "delivered":
        return <Badge className="bg-green-500 hover:bg-green-600">Παραδόθηκε</Badge>;
      case "failed":
        return <Badge variant="destructive">Αποτυχία</Badge>;
      default:
        return <Badge variant="outline">Άγνωστο</Badge>;
    }
  };

  const navigateToStats = () => {
    navigate("/admin/messages/statistics");
  };
  
  const handleRefresh = () => {
    fetchSMSDeliveryStatuses();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <MessageSquare className="h-5 w-5 mr-2 text-primary" />
            Παρακολούθηση SMS
          </div>
          <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
        <CardDescription>
          Ιστορικό των τελευταίων 10 μηνυμάτων SMS
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="bg-muted/30 p-3 rounded-lg">
              <div className="text-sm text-muted-foreground">Απεσταλμένα</div>
              <div className="text-2xl font-bold mt-1">{sentSMS.length}</div>
            </div>
            <div className="bg-muted/30 p-3 rounded-lg">
              <div className="text-sm text-muted-foreground">Αναγνώσεις</div>
              <div className="text-2xl font-bold mt-1">{viewedSMS.length}</div>
            </div>
          </div>
          
          <div className="py-2 px-3 bg-muted/30 rounded-lg mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Ποσοστό αλληλεπίδρασης</span>
              <Badge variant="outline" className="ml-2">{messageEngagementRate}%</Badge>
            </div>
            <div className="w-full bg-muted h-2 rounded-full mt-2">
              <div 
                className="bg-primary h-2 rounded-full" 
                style={{ width: `${messageEngagementRate}%` }}
              ></div>
            </div>
          </div>
          
          {smsHistory.length > 0 ? (
            <div className="divide-y">
              {smsHistory.map((lead) => (
                <div key={lead.id} className="py-3 flex items-center justify-between">
                  <div className="flex items-center">
                    {getStatusIcon(lead.smsStatus || "unknown")}
                    <div className="ml-3">
                      <div className="font-medium">{lead.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(lead.updatedAt), { addSuffix: true, locale: el })}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {lead.viewed && <Eye className="h-4 w-4 text-blue-500" />}
                    {getStatusBadge(lead.smsStatus || "unknown")}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              Δεν υπάρχουν καταγεγραμμένα μηνύματα SMS
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          variant="outline" 
          className="w-full" 
          onClick={navigateToStats}
        >
          <BarChart2 className="h-4 w-4 mr-2" />
          Αναλυτικά Στατιστικά
        </Button>
      </CardFooter>
    </Card>
  );
};

export default SMSTracker;
